import { Button } from "@2u-landing-pages/components/Button";
import { Card } from "@2u-landing-pages/components/Card";
import { ContentLayout } from "@2u-landing-pages/components/ContentLayout";
import { ProductCard } from "@2u-landing-pages/components/ProductCard";
import { TemplateWrapper } from "@2u-landing-pages/components/TemplateWrapper";
import type {
  TypeCardWithoutUnresolvableLinksResponse,
  TypeProduct_cardWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { useIntersectionObserver, useMediaQuery } from "@2u-landing-pages/utils";
import EventClient from "@2u-landing-pages/utils/event-client";
import { Container } from "@red-digital/bricks";
import cn from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Slider, { Settings } from "react-slick";
import { CarouselProps, SettingsExtended } from "../../Carousel.types";
import { Pagination } from "../../components/Pagination";

import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
// @ts-ignore
import styles from "./Default.module.scss";

const slide = (sliderRef: React.MutableRefObject<Slider | null>, direction: "next" | "prev") => {
  if (!sliderRef.current) return;
  if (direction === "next") {
    sliderRef.current.slickNext();
  } else {
    sliderRef.current.slickPrev();
  }
};

export const Default: React.FC<CarouselProps> = ({
  location,
  section_heading,
  items,
  section_background_color,
  button_color,
  mode,
  phoneNumber,
  settingsOverride,
  carouselLayoutOverride,
  className = "",
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const sliderRef = useRef<Slider | null>(null);
  const totalSlides = items?.length || 0;
  const cardContentType = items?.[0]?.sys?.contentType?.sys?.id;
  const cardSize =
    (items?.[0]?.fields as { variation?: string })?.variation ||
    (items?.[0]?.fields as { product_card_size?: string })?.product_card_size;
  const carouselLayout = carouselLayoutOverride || cardSize || "large";

  const settings: SettingsExtended = useMemo(() => {
    let baseSettings: SettingsExtended = {
      dots: false,
      prevArrow: <></>,
      nextArrow: <></>,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide: 0,
      draggable: true,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            infinite: true,
            draggable: true,
          },
        },
      ],
    };

    if (settingsOverride) {
      baseSettings = settingsOverride;
    } else {
      if (totalSlides <= 2) {
        (baseSettings.responsive[0].settings as Settings).infinite = false;
      }

      if (totalSlides <= 4 && carouselLayout === "small") {
        baseSettings.infinite = false;
        baseSettings.slidesToShow = totalSlides;
        baseSettings.draggable = false;
        (baseSettings.responsive[0].settings as Settings).slidesToShow = 1;
      }

      if (totalSlides === 4 && carouselLayout === "small") {
        baseSettings.responsive[0].breakpoint = 1160;
      }
    }

    baseSettings.beforeChange = (_: number, newIndex: number) => {
      setActiveIndex(newIndex);
    };

    return baseSettings;
  }, [settingsOverride, totalSlides, carouselLayout]);

  useEffect(() => {
    if (!isMobile && sliderRef.current) {
      sliderRef.current.slickGoTo(0);
    }
  }, [isMobile]);

  const onCardViewed = useCallback((element: HTMLElement) => {
    const position = element.dataset.position;
    const name = element.dataset.name;
    EventClient.elementViewed({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location: location,
        position: position || "",
        text: name ?? "",
      },
    });
  }, []);

  const addObservedRef = useIntersectionObserver(onCardViewed, {
    threshold: 1.0,
  });
  return (
    <TemplateWrapper padded backgroundColor={section_background_color}>
      <Container className={cn(styles.carousel, className)}>
        <ContentLayout
          className={styles.sectionHeader}
          content={section_heading}
          heading={{
            className: styles.heading,
            variant: "heading-2xl",
          }}
          phoneNumber={phoneNumber}
        />
        <Container flex justifyContent="center" className={styles.carouselWrapper}>
          {/* Possibly set up max width based on card and size */}
          <Slider
            ref={sliderRef}
            {...settings}
            className={cn(styles.slider, "carousel-slider", {
              [styles[`slider-${carouselLayout}`]]: !(totalSlides <= 4 && carouselLayout === "small"),
              [styles.productCardSlider]: cardContentType === "product_card",
              [styles.twoCardsLayout]: totalSlides === 2 && !settingsOverride,
            })}
          >
            {items?.map((item, index) => {
              if (!item) return null;
              const contentType = item?.sys?.contentType?.sys?.id;
              if (contentType === "product_card") {
                return (
                  <ProductCard
                    key={item.sys.id}
                    ref={(el) => {
                      if (el) {
                        el.dataset.location = "Carousel";
                        el.dataset.position = index.toString();
                        el.dataset.name = item.fields.name;
                        addObservedRef(el);
                      }
                    }}
                    product={item as TypeProduct_cardWithoutUnresolvableLinksResponse}
                    position={index + 1}
                    location={location}
                    mode={mode}
                    standalone={true}
                    disableRecommendedFlag={true}
                    className={styles.productCardInCarousel}
                  />
                );
              }
              if (contentType === "card") {
                return (
                  <Card
                    key={item.sys.id}
                    ref={(el) => {
                      if (el) {
                        el.dataset.location = "Carousel";
                        el.dataset.position = index.toString();
                        el.dataset.name = item.fields.name;
                        addObservedRef(el);
                      }
                    }}
                    className={styles.card}
                    entry={item as TypeCardWithoutUnresolvableLinksResponse}
                    phoneNumber={phoneNumber}
                    variationOverride={carouselLayoutOverride}
                  />
                );
              }
              return null;
            })}
          </Slider>
        </Container>
        <div
          className={cn("carousel-controls", {
            [styles[`controls-${settings.responsive[0].breakpoint}`]]: !settings.enableControls,
            [styles.controlsEnabled]:
              (totalSlides > 4 && carouselLayout === "small") ||
              (totalSlides > 1 && carouselLayout === "medium") ||
              (totalSlides > 1 && carouselLayout === "large") ||
              (settings.enableControls && totalSlides > 1),
          })}
        >
          <Container flex alignItems="center" justifyContent="center">
            <Button
              variant="filled"
              iconSelection={{ icon: "chevron-left" }}
              size="small"
              color={button_color || "primary"}
              location={location}
              onClick={() => slide(sliderRef, "prev")}
            />
            <Pagination activeIndex={activeIndex} totalSlides={totalSlides} sliderRef={sliderRef} />
            <Button
              variant="filled"
              iconSelection={{ icon: "chevron-right" }}
              size="small"
              color={button_color || "primary"}
              location={location}
              onClick={() => slide(sliderRef, "next")}
            />
          </Container>
        </div>
      </Container>
    </TemplateWrapper>
  );
};
